// Класс SmartSticky позволяет автоматически закреплять элемент в рамках
// родительского блока с возможностью умной прокрутки для просмотра
// закреплённого элемента целиком, когда он не помещается на экране.
// 
// Сделан на базе плагина jQuery Sticky-kit
// http://leafo.net/sticky-kit/
// 
// Для работы нужна поддержка `Map` и `CustomEvent`. Для Map не требуется
// поддержка итераторов, т. к. используется только метод `.forEach`.

import throttleToFrame from '~/utils/events/throttleToFrame';
import {calcOuterHeight, calcOuterWidth} from '~/utils/nodes/OuterSizes';

/**
 * Автоматическое закрепление элемента в рамках родительского блока
 */
class SmartSticky
{
	/**
	 * Класс закреплённого элемента
	 * @const
	 */
	public static CLASS_STUCK: string = 'stuck';
	/**
	 * Класс прилепленного снизу
	 * @const
	 */
	public static CLASS_STUCK_BOTTOM: string = 'stuck-bottom';
	/**
	 * Класс автоматической заглушки для закреплённого элемента
	 * @const
	 */
	public static CLASS_PLACEHOLDER: string = 'smartsticky-placeholder';
	/**
	 * Событие закрепления элемента
	 * @const
	 */
	public static EVENT_STUCK: string = 'smartsticky:stuck';
	/**
	 * Событие закрепления элемента снизу
	 * @const
	 */
	public static EVENT_STUCK_BOTTOM: string = 'smartsticky:bottom';
	/**
	 * Событие открепления элемента
	 * @const
	 */
	public static EVENT_UNSTUCK: string = 'smartsticky:unstuck';
	/**
	 * Событие открепления элемента снизу
	 * @const
	 */
	public static EVENT_UNSTUCK_BOTTOM: string = 'smartsticky:unbottom';
	/**
	 * Интервал в милисекундах, который должен пройти между событиями
	 * прокрутки, чтобы считать, что началась новая прокрутка
	 * @const
	 */
	public static TICK_START_INTERVAL: number = 1000;
	
	/**
	 * Параметры по умолчанию
	 * @const
	 */
	private static DEFAULT_OPTIONS: Partial<SmartStickyOptions> = {
		offsetTop: 0,
		offsetBottom: 0,
		innerScrolling: true,
		innerScrollingOffsetBottom: 0,
		bottoming: true,
		createPlaceholder: true,
		recalcOnScrollStarts: true,
		stickWhenEntireHeight: false,
		moveIntoFloat: false,
	};
	/** Экземпляры класса */
	private static instances: Map<HTMLElement, SmartSticky> = new Map();
	
	/** Параметры экземпляра */
	private options: SmartStickyOptions;
	/** Не выполнять ни каких действий? */
	private disabled: boolean;
	/** Элемент закреплён? */
	private stuck: boolean;
	/** Элемент закреплён снизу? */
	private bottomed: boolean;
	/** Счётчик для пересчёта на циклах прокрутки */
	private recalcOnTickCounter: number;
	/** Время (timestamp) последнего цикла прокрутки */
	private lastTickTime: number;
	
	/** Высота всего документа */
	private lastDocumentHeight: number;
	/** Положение прокрутки */
	private lastScrollPos: number;
	/** Текущий отступ элемента от верха экрана (с учётом настройки и прокрутки) */
	private currentOffsetTop: number;
	
	/** Отступ контента родительского элемента от верха документа */
	private parentTop: number;
	/** Высота области контента родительского элемента */
	private parentHeight: number;
	/** Отступ контента родительского элемента снизу */
	private parentPaddingBottom: number;
	
	/** Закрепляемый элемент */
	private element: HTMLElement;
	/** Внешняя высота элемента */
	private elementHeight: number;
	/** Отступ элемента от верха документа */
	private elementTop: number;
	/** Значение CSS-свойства `float` элемента */
	private elementCssFloat: string | null;
	/** Элемент заглушки для закреплённого состояния */
	private placeholderElement: HTMLElement;
	
	/**
	 * Обновить и пересчитать все экземпляры класса в документе
	 * 
	 * @returns
	 */
	public static updateAll(): boolean
	{
		if ( SmartSticky.instances.size < 1 )
		{
			return false;
		}
		
		SmartSticky.instances.forEach(
			( instance: SmartSticky ) => instance.update(),
		);
		
		return true;
	}
	
	/**
	 * Автоматическое закрепление элемента в рамках родительского блока
	 * 
	 * @param element Элемент для закрепления
	 * @param [options={}] Параметры закрепления
	 */
	public constructor( element: HTMLElement, options: Partial<SmartStickyOptions> = {} )
	{
		if ( SmartSticky.instances.has( element ) )
		{
			return SmartSticky.instances.get( element ) as SmartSticky;
		}
		
		SmartSticky.instances.set( element, this );
		
		this.options = Object.assign( {}, SmartSticky.DEFAULT_OPTIONS, options ) as SmartStickyOptions;
		
		if ( !this.options.parentElement )
		{
			if ( element.parentElement )
			{
				this.options.parentElement = element.parentElement;
			}
			else
			{
				this.options.parentElement = document.body;
			}
		}
		
		if ( this.options.createPlaceholder )
		{
			this.placeholderElement = document.createElement( 'div' );
			this.placeholderElement.className = SmartSticky.CLASS_PLACEHOLDER;
		}
		
		this.element = element;
		
		this.disabled = false;
		this.stuck = false;
		this.bottomed = false;
		this.lastTickTime = 0;
		this.currentOffsetTop = this.options.offsetTop || 0;
		
		this.recalc();
		this.registerHandlers();
		setTimeout( () => this.tick(), 0 );
	}
	
	/**
	 * Удалить все связи, созданные классом и остановить его работу
	 */
	public destroy(): void
	{
		this.disabled = true;
		
		this.unregisterHandlers();
		this.unstick();
		this.options.parentElement.style.position = '';
		
		SmartSticky.instances.delete( this.element );
		
		this.placeholderElement = null as any;
		this.element = null as any;
		this.options.parentElement = null as any;
		this.options = null as any;
	}
	
	/**
	 * Обновить и пересчитать состояние закрепления элемента
	 * 
	 * @returns Обновление прошло успешно?
	 */
	public update(): boolean
	{
		return this.recalc() && this.tick();
	}
	
	/**
	 * Приостановить работу класса
	 * 
	 * @returns Успешно?
	 */
	public disable(): boolean
	{
		if ( this.stuck )
		{
			this.unstick();
		}
		
		this.disabled = true;
		
		return true;
	}
	
	/**
	 * Возобновить работу класса
	 * 
	 * @returns Успешно?
	 */
	public enable(): boolean
	{
		if ( !this.element )
		{
			return false;
		}
		
		this.disabled = false;
		
		return true;
	}
	
	/**
	 * Зарегистрировать обработчики событий
	 */
	private registerHandlers(): void
	{
		const onScroll = throttleToFrame( () => this.tick() );
		const onResize = throttleToFrame( () => this.update() );
		
		window.addEventListener( 'scroll', onScroll );
		window.addEventListener( 'touchmove', onScroll );
		window.addEventListener( 'resize', onResize );
		
		this.unregisterHandlers = (): void =>
		{
			window.removeEventListener( 'scroll', onScroll );
			window.removeEventListener( 'touchmove', onScroll );
			window.removeEventListener( 'resize', onResize );
			this.unregisterHandlers = () => void 0;
		};
	}
	
	/**
	 * Снять зарегистрированные обработчики событий
	 */
	// tslint:disable-next-line:prefer-function-over-method
	private unregisterHandlers(): void
	{
		// Создаётся в registerHandlers
	}
	
	/**
	 * Пересчитать параметры закрепления
	 * 
	 * @returns Пересчёт завершён?
	 */
	private recalc(): boolean
	{
		if ( this.disabled )
		{
			return false;
		}
		
		this.recalcOnTickCounter = this.options.recalcEvery;
		
		this.lastDocumentHeight = document.documentElement.offsetHeight;
		
		const parentComputedStyle = window.getComputedStyle( this.options.parentElement );
		const borderTop = parseInt( parentComputedStyle.borderTopWidth || '0', 10 );
		const paddingTop = parseInt( parentComputedStyle.paddingTop || '0', 10 );
		this.parentPaddingBottom = parseInt( parentComputedStyle.paddingBottom || '0', 10 )
			+ this.options.offsetBottom;
		
		this.parentTop = this.options.parentElement.getBoundingClientRect().top
			+ window.scrollY + borderTop + paddingTop;
		this.parentHeight = this.options.parentElement.clientHeight
			- this.options.offsetBottom;
		
		let temporarilyUnpinned: boolean = false;
		
		if ( this.stuck )
		{
			this.unstick();
			temporarilyUnpinned = true;
		}
		
		const elementComputedStyle = window.getComputedStyle( this.element );
		
		this.elementTop = this.element.getBoundingClientRect().top
			+ window.scrollY - ( parseInt( elementComputedStyle.marginTop || '0', 10 ) || 0 )
			- this.options.offsetTop;
		this.elementHeight = Math.ceil( calcOuterHeight( this.element, true ) );
		this.elementCssFloat = elementComputedStyle.cssFloat;
		
		if ( this.placeholderElement )
		{
			const placeholderStyle = this.placeholderElement.style;
			
			placeholderStyle.width = calcOuterWidth( this.element, true ) + 'px';
			placeholderStyle.height = this.elementHeight + 'px';
			placeholderStyle.display = elementComputedStyle.display;
			placeholderStyle.verticalAlign = elementComputedStyle.verticalAlign;
			placeholderStyle.cssFloat = this.elementCssFloat;
		}
		
		if ( temporarilyUnpinned )
		{
			this.tick();
		}
		
		return true;
	}
	
	/**
	 * Проверить и выполнить пересчёт, если нужно
	 * 
	 * @returns Выполнен пересчёт?
	 */
	private checkRecalc(): boolean
	{
		if ( this.options.recalcOnScrollStarts )
		{
			const lastTickTime = this.lastTickTime;
			const timestamp = Date.now();
			this.lastTickTime = timestamp;
			
			if ( timestamp > (lastTickTime + SmartSticky.TICK_START_INTERVAL) )
			{
				this.recalc();
				
				return true;
			}
		}
		
		if ( this.recalcOnTickCounter )
		{
			--this.recalcOnTickCounter;
			
			if ( this.recalcOnTickCounter <= 0 )
			{
				this.recalcOnTickCounter = this.options.recalcEvery;
				this.recalc();
				
				return true;
			}
		}
		
		if ( document.documentElement.offsetHeight !== this.lastDocumentHeight )
		{
			this.recalc();
			
			return true;
		}
		
		return false;
	}
	
	/**
	 * Шаг события прокрутки
	 * 
	 * @returns Обработка завершена?
	 */
	private tick(): boolean
	{
		if ( this.disabled )
		{
			return false;
		}
		
		this.checkRecalc();
		
		if (
			!this.options.stickWhenEntireHeight
			&& ( this.elementHeight === this.parentHeight )
		)
		{
			// Не закреплять элемент, если он занимает всю высоту родительского элемента
			
			if ( this.stuck )
			{
				this.unstick();
			}
			
			return false;
		}
		
		const scrollPos = window.scrollY;
		let delta: number = 0;
		
		if ( this.lastScrollPos != null )
		{
			delta = scrollPos - this.lastScrollPos;
		}
		
		this.lastScrollPos = scrollPos;
		
		const isWillBottom = () => (
			(scrollPos + this.elementHeight + this.currentOffsetTop)
			> (this.parentHeight + this.parentTop)
		);
		let willBottom: boolean = false;
		
		if ( this.stuck )
		{
			// Проверка закреплённого элемента
			
			if ( this.options.bottoming )
			{
				willBottom = isWillBottom();
				
				if ( this.bottomed && !willBottom )
				{
					this.unbottom();
				}
			}
			
			if ( scrollPos <= this.elementTop )
			{
				this.unstick();
				this.currentOffsetTop = this.options.offsetTop;
			}
			
			if ( this.options.innerScrolling )
			{
				// Обновить отступ при прокрутке закреплённого элемента
				
				const windowHeight = window.innerHeight;
				
				if (
					!this.bottomed
					// Элемент больше экрана
					&& (
						(this.elementHeight + this.options.offsetTop
							+ this.options.innerScrollingOffsetBottom)
						> windowHeight
					)
				)
				{
					this.currentOffsetTop = (
						Math.min(
							this.options.offsetTop,
							Math.max(
								windowHeight - this.elementHeight
									- this.options.innerScrollingOffsetBottom,
								this.currentOffsetTop - delta,
							),
						)
					);
					
					if ( this.stuck )
					{
						this.element.style.top = this.currentOffsetTop + 'px';
					}
				}
			}
		}
		else if ( scrollPos > this.elementTop )
		{
			// Закрепление элемента
			
			this.stick();
		}
		
		if ( this.stuck && this.options.bottoming )
		{
			// Проверка, не нужно ли закрепить снизу
			
			if ( willBottom == null )
			{
				willBottom = isWillBottom();
			}
			
			if ( !this.bottomed && willBottom )
			{
				this.bottom();
			}
		}
		
		return true;
	}
	
	/**
	 * Закрепить элемент
	 */
	private stick(): void
	{
		this.stuck = true;
		
		const elementComputedStyle = window.getComputedStyle( this.element );
		const elementStyle = this.element.style;
		
		elementStyle.width = elementComputedStyle.width;
		elementStyle.top = this.currentOffsetTop + 'px';
		elementStyle.position = 'fixed';
		
		if ( this.placeholderElement )
		{
			const parent = this.element.parentNode;
			
			if ( parent )
			{
				parent.insertBefore( this.placeholderElement, this.element.nextSibling );
				
				if (
					this.options.moveIntoFloat
					&& (
						( this.elementCssFloat === 'left' )
						|| ( this.elementCssFloat === 'right' )
					)
				)
				{
					this.placeholderElement.appendChild( this.element );
				}
			}
		}
		
		this.element.classList.add( SmartSticky.CLASS_STUCK );
		this.element.dispatchEvent(
			new CustomEvent( SmartSticky.EVENT_STUCK ),
		);
	}
	
	/**
	 * Открепить элемент
	 */
	private unstick(): void
	{
		if ( this.bottomed )
		{
			this.unbottom();
		}
		
		this.stuck = false;
		
		if ( this.placeholderElement )
		{
			const parent = this.placeholderElement.parentNode;
			
			if ( parent )
			{
				if (
					this.options.moveIntoFloat
					&& (
						( this.elementCssFloat === 'left' )
						|| ( this.elementCssFloat === 'right' )
					)
				)
				{
					parent.insertBefore( this.element, this.placeholderElement.nextSibling );
				}
				
				parent.removeChild( this.placeholderElement );
			}
		}
		
		const elementStyle = this.element.style;
		
		elementStyle.position = '';
		elementStyle.top = '';
		elementStyle.width = '';
		
		this.element.classList.remove( SmartSticky.CLASS_STUCK );
		this.element.dispatchEvent(
			new CustomEvent( SmartSticky.EVENT_UNSTUCK ),
		);
	}
	
	/**
	 * Закрепить элемент снизу
	 */
	private bottom(): void
	{
		this.bottomed = true;
		
		const parentComputedStyle = window.getComputedStyle( this.options.parentElement );
		
		if ( parentComputedStyle.position === 'static' )
		{
			this.options.parentElement.style.position = 'relative';
		}
		
		const elementStyle = this.element.style;
		
		elementStyle.position = 'absolute';
		elementStyle.top = 'auto';
		elementStyle.bottom = this.parentPaddingBottom + 'px';
		
		this.element.classList.add( SmartSticky.CLASS_STUCK_BOTTOM );
		this.element.dispatchEvent(
			new CustomEvent( SmartSticky.EVENT_STUCK_BOTTOM ),
		);
	}
	
	/**
	 * Открепить элемент снизу
	 */
	private unbottom(): void
	{
		this.bottomed = false;
		
		const elementStyle = this.element.style;
		
		elementStyle.position = 'fixed';
		elementStyle.top = this.currentOffsetTop + 'px';
		elementStyle.bottom = '';
		
		this.element.classList.remove( SmartSticky.CLASS_STUCK_BOTTOM );
		this.element.dispatchEvent(
			new CustomEvent( SmartSticky.EVENT_UNSTUCK_BOTTOM ),
		);
	}
	
}

/**
 * Параметры
 */
interface SmartStickyOptions
{
	/** Отступ для закрепления сверху */
	offsetTop: number;
	/** Отступ для закрепления снизу */
	offsetBottom: number;
	/** Родительский элемент, относительно которого происходит закрепление */
	parentElement: HTMLElement;
	/** Разрешить прокрутку закреплённого элемента? */
	innerScrolling: boolean;
	/** Отступ снизу при прокрутке закреплённого элемента */
	innerScrollingOffsetBottom: number;
	/** Разрешить закреплять элемент снизу? */
	bottoming: boolean;
	/** Создавать заглушку для закреплённого элемента? */
	createPlaceholder: boolean;
	/** Выполнять пересчёт каждые N циклов события проктурки */
	recalcEvery: number;
	/** Выполнять пересчёт в начале любой прокрутки */
	recalcOnScrollStarts: boolean;
	/** Закреплять элемент, если он занимает всю высоту родительского элемента? */
	stickWhenEntireHeight: boolean;
	/** Перемещать элемент внутрь загшулки, если он размещается с использованием float? */
	moveIntoFloat: boolean;
}

/**
 * Модуль
 */
export {
	SmartSticky as default,
	SmartStickyOptions,
};



// WEBPACK FOOTER //
// ./app/elements/SmartSticky.ts